import { useMemo } from 'react';
import { cn } from '@/lib/utils';
// DCC-A4: dropped `format` from date-fns import — only consumer was
// the `format(selectedDate, 'yyyy-MM-dd')` derivation at L39 (pre-
// edit), now replaced with canonical `selectedDayLabel` prop.
// `getDay(selectedDate)` for day-of-week index extraction at L40
// preserved (DCD-A3 OUT_OF_SCOPE: Date-input contract for date-fns
// weekday helpers). `calculateTechCapacity(tech, selectedDate)` calls
// at L49/L79 also preserved (utility's Date-input contract).
import { TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { calculateOBLI, formatOBLIScore, getOBLIColorClasses } from '@/utils/obliCalculation';
// Phase G2B — canonical live-load helpers. OBLI base now reflects live
// projected workload (includes run-long + bay-blocking pause; held-in-
// parking / non-bay holds correctly contribute 0). Penalty weights
// (waiter +3, specialty +2, short-staff +10/+5) are unchanged.
import { clock } from '@/utils/clock';
import { assembleOBLIDay } from '@/lib/obli/obliDayInputs';

interface OBLIBucketProps {
  technicians: any[];
  tickets: any[];
  selectedDate: Date;
  selectedDayLabel: string;
  shopHours?: Record<number, { open: string; close: string }>;
  holidays?: string[];
  bays?: any[];
  fullWidth?: boolean;
  shopTimezone?: string;
  /** Work-role answer: whose time is MECHANICAL capacity (null/absent = everyone). */
  mechanicalTechnicianIds?: ReadonlySet<string> | null;
  /** Roster + work-role answer known for this shop; otherwise "Capacity unavailable". */
  capacityInputsReady?: boolean;
}

/**
 * OBLI (OptiBay Load Index) - Composite metric for day stress level
 * Low score (0-25) = Light/smooth day (green)
 * Medium score (26-50) = Moderate load (yellow)
 * High score (51-75) = Heavy load (orange)
 * Severe score (76-100) = Overloaded (red)
 */
export const OBLIBucket = ({
  technicians,
  tickets,
  selectedDate,
  selectedDayLabel,
  shopHours = {},
  holidays = [],
  bays = [],
  fullWidth = false,
  shopTimezone,
  mechanicalTechnicianIds = null,
  capacityInputsReady = true,
}: OBLIBucketProps) => {
  // DCC-A4: route DB-binding date string through canonical SoT.
  // Core-100 R4 (CORE-AV): the ONE input assembler, shared with the Dayboard indicator (assembleOBLIDay). The
  // canonical day-ticket checks (DMC-D3c-1 isTicketDeleted, backlog, completed) and the capacity authority live there.
  const day = useMemo(() => assembleOBLIDay({
    technicians, tickets, selectedDate, dayLabel: selectedDayLabel, shopTimezone, shopHours, holidays,
    mechanicalTechnicianIds, capacityInputsReady, nowMs: clock.nowMs(),
  }), [technicians, tickets, selectedDate, selectedDayLabel, shopTimezone, shopHours, holidays, mechanicalTechnicianIds, capacityInputsReady]);
  const { dayTickets, totalCapacity } = day;
  const getTechsWorkingOnDate = day.workingTechs;
  const mechanicalTechCount = day.roster.length;
  const capacityComplete = day.unknownHoursCount === 0;
  const techsWithUnknownHours = { length: day.unknownHoursCount };
  const { bookedMinutes } = day.input;
  const specialtyCounts = { specialtyCount: day.input.specialtyJobCount, waiterCount: day.input.waiterCount };
  const obliResult = useMemo(() => calculateOBLI(day.input), [day]);

  const obliScore = obliResult.score;
  const unavailable = obliResult.state === 'unavailable';
  const colors = getOBLIColorClasses(unavailable ? 0 : obliScore);
  const utilizationPct = obliResult.utilizationPercent;

  return (
    <div className={cn(
      "p-4 rounded-lg border transition-all",
      colors.bg,
      colors.glow,
      fullWidth && "col-span-full"
    )}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Activity className={cn("h-5 w-5", colors.text)} />
            <h3 className="font-semibold text-sm">OBLI (OptiBay Load Index)</h3>
          </div>
          <p className={cn("text-2xl font-bold", colors.text)} data-testid="obli-bucket-score" data-obli-state={obliResult.state}>
            {unavailable ? formatOBLIScore(obliResult) : `${obliScore}/100`}
          </p>
          <p className={cn("text-sm font-medium", colors.text)}>
            {obliResult.label}
          </p>
          {/*
            CORE-AW §44: the index is a ratio against capacity. Where some of
            that capacity is unrecorded the ratio is over a partial
            denominator, and saying so is the difference between a measure and
            a claim.
          */}
          {!capacityComplete && (
            <p
              className="text-xs text-muted-foreground mt-1"
              data-testid="obli-capacity-incomplete"
            >
              Based on known capacity only — {techsWithUnknownHours.length} technician
              {techsWithUnknownHours.length === 1 ? "'s" : "s'"} working hours are not set
            </p>
          )}
        </div>
        
        <div className="text-right">
          <div className="flex items-center gap-1 justify-end">
            {utilizationPct >= 100 ? (
              <TrendingUp className="h-4 w-4 text-destructive" />
            ) : utilizationPct >= 70 ? (
              <TrendingUp className="h-4 w-4 text-warning" />
            ) : (
              <TrendingDown className="h-4 w-4 text-success" />
            )}
            <span
              className="text-sm font-medium"
              title="Live load ÷ capacity (includes run-long + bay-blocking pause)"
            >
              {unavailable ? 'Capacity unavailable' : `${utilizationPct}% capacity`}
            </span>
          </div>
          <p
            className="text-xs text-muted-foreground"
            title="Live load (live projected workload, includes run-long + bay-blocking pause) / capacity"
          >
            {Math.round(bookedMinutes / 60)}h / {unavailable ? '—' : `${Math.round(totalCapacity / 60)}h`}
          </p>
        </div>
      </div>

      {fullWidth && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {obliResult.description}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-xs">
            <div>
              <span className="text-muted-foreground">Techs Available:</span>
              <span className="ml-1 font-medium">{getTechsWorkingOnDate.length}/{mechanicalTechCount}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Tickets:</span>
              <span className="ml-1 font-medium">{dayTickets.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Waiters:</span>
              <span className="ml-1 font-medium">{dayTickets.filter(t => t.type === 'Waiter').length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Specialty Jobs:</span>
              <span className="ml-1 font-medium">{specialtyCounts.specialtyCount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
